<template>
    <section v-if="visible" class="rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
        <div class="flex items-start gap-3">
            <div class="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 shrink-0">
                <svg class="w-5 h-5 text-blue-600 dark:text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
            </div>

            <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                    <h3 class="text-sm font-semibold text-blue-900 dark:text-blue-100">Market intelligence</h3>
                    <span v-if="loading" class="text-xs text-blue-700 dark:text-blue-300">
                        <svg class="inline-block animate-spin w-3 h-3 mr-1 align-text-top" fill="none" viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" class="opacity-25"></circle>
                            <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="3" class="opacity-75"></path>
                        </svg>
                        synthesizing…
                    </span>
                    <span v-else-if="cached" class="text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-300">cached</span>
                </div>

                <p v-if="error" class="text-xs text-red-700 dark:text-red-300">{{ error }}</p>

                <p v-else-if="!loading && narrative" class="text-sm text-gray-800 dark:text-gray-100">
                    {{ narrative }}
                </p>

                <!-- Numerical benchmarks -->
                <div v-if="!loading && hasNumbers" class="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div class="rounded bg-white dark:bg-slate-800 px-3 py-2 border border-gray-200 dark:border-slate-700">
                        <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Avg asking</div>
                        <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {{ data.avg_asking_rent_psf != null ? '$' + data.avg_asking_rent_psf.toFixed(2) + '/SF/yr' : '—' }}
                        </div>
                    </div>
                    <div class="rounded bg-white dark:bg-slate-800 px-3 py-2 border border-gray-200 dark:border-slate-700">
                        <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Vacancy</div>
                        <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {{ data.vacancy_rate_pct != null ? data.vacancy_rate_pct.toFixed(1) + '%' : '—' }}
                        </div>
                    </div>
                    <div class="rounded bg-white dark:bg-slate-800 px-3 py-2 border border-gray-200 dark:border-slate-700">
                        <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Trend (12 mo)</div>
                        <div class="text-sm font-semibold capitalize" :class="trendClass">
                            {{ data.trend || '—' }}
                        </div>
                    </div>
                    <div class="rounded bg-white dark:bg-slate-800 px-3 py-2 border border-gray-200 dark:border-slate-700">
                        <div class="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">Sample</div>
                        <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 capitalize">
                            {{ data.sample_size_qualifier || '—' }}
                        </div>
                    </div>
                </div>

                <!-- Submarket callouts -->
                <ul v-if="!loading && (data.submarket_callouts || []).length" class="mt-3 space-y-1 text-xs text-gray-700 dark:text-gray-300">
                    <li v-for="(c, i) in data.submarket_callouts" :key="i" class="flex gap-2">
                        <span class="text-blue-500 dark:text-blue-300">•</span>
                        <span>{{ c }}</span>
                    </li>
                </ul>

                <!-- Compliance disclaimer — always rendered with output -->
                <p v-if="!loading && (narrative || hasNumbers)" class="mt-3 text-[10px] italic text-gray-500 dark:text-gray-400">
                    {{ data.disclaimer || 'AI-generated from publicly available data. Verify with a broker before relying.' }}
                    <span v-if="data.provider" class="not-italic ml-1">({{ data.provider }})</span>
                </p>
            </div>
        </div>
    </section>
</template>

<script>
export default {
    name: 'MarketIntelligence',
    props: {
        // Reactive criteria object from the search store. The component
        // re-fetches when the criteria signature changes (state, city, type,
        // sf range — not which brokerages are selected).
        criteria: { type: Object, required: true },

        // Optional trigger: when the user runs a search, the parent toggles
        // `runToken` (e.g. increment a counter) to request a refresh.
        runToken: { type: [Number, String], default: 0 },
    },

    data() {
        return {
            loading: false,
            error: null,
            data: this.emptyPayload(),
        };
    },

    computed: {
        signature() {
            const c = this.criteria || {};
            return [c.property_type, c.state, c.city, c.min_sf, c.max_sf].join('|');
        },
        narrative() { return this.data?.narrative || ''; },
        cached()    { return Boolean(this.data?.cached); },
        hasNumbers() {
            return this.data
                && (this.data.avg_asking_rent_psf != null
                    || this.data.vacancy_rate_pct != null
                    || this.data.trend
                    || this.data.sample_size_qualifier);
        },
        trendClass() {
            return {
                'text-green-700  dark:text-green-300':  this.data.trend === 'rising',
                'text-gray-700   dark:text-gray-200':   this.data.trend === 'stable',
                'text-red-700    dark:text-red-300':    this.data.trend === 'declining',
            };
        },
        // Visible whenever there's something to show or in flight.
        visible() {
            return this.loading || this.error || this.narrative || this.hasNumbers;
        },
    },

    watch: {
        runToken() { this.fetch(); },
    },

    methods: {
        emptyPayload() {
            return {
                narrative: '',
                avg_asking_rent_psf: null,
                vacancy_rate_pct: null,
                trend: null,
                submarket_callouts: [],
                sample_size_qualifier: null,
                provider: null,
                disclaimer: '',
                cached: false,
            };
        },
        async fetch() {
            const c = this.criteria || {};
            if (!c.property_type || !c.state || !c.city) return;

            this.loading = true;
            this.error = null;
            try {
                const { data } = await axios.post('/api/listing-search/market-analysis', c);
                if (data?.success) {
                    this.data = data.data;
                } else {
                    this.error = data?.data?.error || 'Market analysis unavailable.';
                }
            } catch (e) {
                this.error = e?.response?.data?.data?.error
                    || e?.response?.data?.message
                    || 'Market analysis unavailable.';
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
